import { useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { ClipboardList, Copy, Eye, LayoutGrid, Plus } from 'lucide-react';

import { Badge } from '@/components/ui/Badge';
import { Button } from '@/components/ui/Button';
import { EmptyState } from '@/components/ui/EmptyState';
import { ProgressBar } from '@/components/ui/ProgressBar';
import { QueryError } from '@/components/ui/QueryError';
import { Loader } from '@/components/ui/Loader';
import { SkeletonLines } from '@/components/ui/Skeleton';
import {
  SCHOOL_STATUS_LABEL,
  SCHOOL_STATUS_TONE,
  type InstituteStatus,
} from '@/constants/institute';
import { useApiQuery } from '@/hooks/useApiQuery';
import { useDelayedLoading } from '@/hooks/useDelayedLoading';
import { toast } from '@/hooks/useToast';
import { ApiRequestError } from '@/lib/apiClient';
import { formatCurrency } from '@/lib/format';
import { cn, getNameInitials } from '@/lib/utils';
import type {
  FeatureSwitchPayload,
  InstituteFeatureBoard,
  PlatformFeature,
} from '@/types/platformFeature';
import { DeckPageHeader } from '../../components/DeckPageHeader';
import { DeckStatTile } from '../../components/DeckStatTile';
import { DeckSurface } from '../../components/DeckSurface';
import { useIsSuperAdmin } from '../../hooks/useIsSuperAdmin';
import { institutesApi } from '../../institutes/api/institutes.api';
import { featuresApi } from '../api/features.api';
import { CatalogueEntryModal } from '../components/CatalogueEntryModal';
import { CopyFeaturesModal } from '../components/CopyFeaturesModal';
import { ChecklistToolbar, FeatureChecklist } from '../components/FeatureChecklist';
import { InstitutePicker } from '../components/InstitutePicker';

/** How many institutes the picker holds. The console has tens, not thousands. */
const PICKER_SIZE = 100;

/**
 * What each customer has asked for, and where it stands.
 *
 * One screen, two panes: every institute on the left with its account status,
 * and on the right the whole catalogue with this customer's requirements marked
 * on it. One switch is kept per pairing —
 *
 *   active     they can use it, so a capability can be taken back out of
 *              their hands without losing the record of having asked.
 *
 * **The switch does not gate the institute's app yet.** It is recorded here and
 * read here; nothing in the institute, teacher or student consoles consults it.
 * That was the decision when this was built — the tracker had to exist before
 * anything could be gated on it — and it is why nothing outside this feature
 * folder changed.
 *
 * Every write answers with the whole board, so this page replaces its state
 * rather than patching it: a total can never end up disagreeing with the rows
 * under it, and two people working at once see the same thing on next load.
 */
export function FeatureTrackerPage() {
  // --- who we are looking at ------------------------------------------------
  const [searchParams] = useSearchParams();
  // Every write on this screen is SUPER_ADMIN only; support reads the board.
  const canEdit = useIsSuperAdmin();
  const [search, setSearch] = useState('');
  /** Empty means every status — the shape `InstituteListParams` already uses. */
  const [status, setStatus] = useState<InstituteStatus | ''>('');
  /**
   * An institute in the URL opens on that institute's board.
   *
   * This is what makes the customer list's features band worth pressing: it
   * lands on the tracker already showing that school rather than on whichever
   * one happens to sort first, with the reader left to find it in the picker.
   * Read once, as the initial selection — the picker owns it after that, so
   * choosing another institute does not rewrite the address bar.
   */
  const [linkedUuid] = useState(() => searchParams.get('institute'));
  const [selectedUuid, setSelectedUuid] = useState<string | null>(linkedUuid);

  const {
    data: institutes,
    isLoading: isLoadingInstitutes,
    error: instituteError,
    refetch: refetchInstitutes,
  } = useApiQuery(
    () => institutesApi.list({ page: 1, limit: PICKER_SIZE, search, status }),
    [search, status],
  );

  const rows = useMemo(() => institutes?.items ?? [], [institutes]);

  // Settles on the first institute once the list arrives, and moves off one
  // that a filter has just taken away rather than showing a board for a row
  // nobody can see.
  //
  // Except the institute the link named. The picker holds only the first
  // hundred, narrowed by its own search and status, so "not in the picker" is
  // no evidence the link is wrong — and swapping in the first row put a
  // different customer's board under a link that named this one. The board is
  // fetched by uuid whatever the picker holds, and a uuid that is no institute
  // fails there, as an error, rather than quietly becoming someone else.
  useEffect(() => {
    if (selectedUuid !== null && selectedUuid === linkedUuid) return;
    if (rows.length === 0) {
      setSelectedUuid(null);
      return;
    }
    if (!selectedUuid || !rows.some((row) => row.uuid === selectedUuid)) {
      setSelectedUuid(rows[0].uuid);
    }
  }, [rows, selectedUuid, linkedUuid]);

  // --- their board ----------------------------------------------------------
  const {
    data: fetchedBoard,
    isLoading: isLoadingBoard,
    error: boardError,
    refetch: refetchBoard,
  } = useApiQuery(
    () => (selectedUuid ? featuresApi.board(selectedUuid) : Promise.resolve(null)),
    [selectedUuid],
  );

  /**
   * The board as the screen holds it.
   *
   * Seeded from the request and then replaced by whatever a write answers with,
   * so a tick takes effect immediately without a second round trip — and
   * without this page ever computing a total for itself.
   */
  const [board, setBoard] = useState<InstituteFeatureBoard | null>(null);
  useEffect(() => setBoard(fetchedBoard), [fetchedBoard]);

  /** The institute being loaded, named on the loader while the old board waits. */
  const switchingTo = rows.find((row) => row.uuid === selectedUuid)?.displayName;
  /**
   * Whether the switch is slow enough to show. Most boards answer before a
   * loader would register, and drawing one for a frame is what read as the page
   * blinking; the board is still held disabled for the whole load regardless.
   */
  const showSwitchLoader = useDelayedLoading(isLoadingBoard && board !== null);
  /**
   * No institute chosen yet, but one is about to be: the list is still loading,
   * or has arrived and the effect above picks its first row on the next render.
   * "Choose an institute" is for a list with nothing in it, not this moment —
   * showing it here flashed it on every visit.
   */
  const isAwaitingSelection = selectedUuid === null && (isLoadingInstitutes || rows.length > 0);

  /** The row being written, so only that one goes busy. */
  const [pendingUuid, setPendingUuid] = useState<string | null>(null);
  const [isBulkBusy, setIsBulkBusy] = useState(false);

  async function runOnRow(featureUuid: string, action: () => Promise<InstituteFeatureBoard>) {
    if (pendingUuid || isBulkBusy) return;
    setPendingUuid(featureUuid);

    try {
      setBoard(await action());
    } catch (caught) {
      // The server's messages name the reason — a feature nobody asked for, an
      // institute that has gone — so they are shown as they are written.
      toast.error(
        'Could not save that change',
        caught instanceof ApiRequestError ? caught.message : 'Something went wrong. Please try again.',
      );
      // Whatever the screen was showing may now be wrong; go back to the server
      // rather than leave a half-applied board on screen.
      refetchBoard();
    } finally {
      setPendingUuid(null);
    }
  }

  async function runOnAll(payload: FeatureSwitchPayload, done: string) {
    if (!selectedUuid || pendingUuid || isBulkBusy) return;
    setIsBulkBusy(true);

    try {
      const next = await featuresApi.setAll(selectedUuid, payload);
      setBoard(next);
      toast.success(done, `${next.institute.displayName} · ${next.summary.tracked} features`);
    } catch (caught) {
      toast.error(
        'Could not apply that to every feature',
        caught instanceof ApiRequestError ? caught.message : 'Something went wrong. Please try again.',
      );
      refetchBoard();
    } finally {
      setIsBulkBusy(false);
    }
  }

  // --- picking what to add --------------------------------------------------
  /**
   * The features ticked for adding.
   *
   * Cleared whenever the board is replaced: a uuid that has just become tracked
   * is no longer selectable, and one left in here would keep the Add button
   * offering something already in.
   */
  const [selected, setSelected] = useState<Set<string>>(new Set());
  useEffect(() => setSelected(new Set()), [fetchedBoard]);

  function toggleSelected(featureUuid: string) {
    setSelected((current) => {
      const next = new Set(current);
      if (next.has(featureUuid)) next.delete(featureUuid);
      else next.add(featureUuid);
      return next;
    });
  }

  function selectAll(next: boolean) {
    setSelected(
      next
        ? new Set((board?.features ?? []).filter((f) => !f.isTracked).map((f) => f.uuid))
        : new Set(),
    );
  }

  async function addSelected() {
    if (!selectedUuid || selected.size === 0 || pendingUuid || isBulkBusy) return;
    const count = selected.size;
    setIsBulkBusy(true);

    try {
      const next = await featuresApi.add(selectedUuid, [...selected]);
      setBoard(next);
      setSelected(new Set());
      toast.success(
        `${count} feature${count === 1 ? '' : 's'} added`,
        `${next.institute.displayName} now tracks ${next.summary.tracked}.`,
      );
    } catch (caught) {
      toast.error(
        'Could not add those features',
        caught instanceof ApiRequestError ? caught.message : 'Something went wrong. Please try again.',
      );
      refetchBoard();
    } finally {
      setIsBulkBusy(false);
    }
  }

  /**
   * Handing this institute's requirements to another.
   *
   * The dialog writes to boards other than the one on screen, so nothing here
   * is refetched afterwards — this institute's tracker is untouched by it.
   */
  const [isCopyOpen, setIsCopyOpen] = useState(false);

  // --- the catalogue form ---------------------------------------------------
  const [isCatalogueOpen, setIsCatalogueOpen] = useState(false);
  /** The row being corrected, or null when the form is adding a new one. */
  const [editing, setEditing] = useState<PlatformFeature | null>(null);

  function openCatalogue(feature: PlatformFeature | null) {
    setEditing(feature);
    setIsCatalogueOpen(true);
  }

  const summary = board?.summary;
  const activePct = summary && summary.tracked > 0 ? (summary.enabled / summary.tracked) * 100 : 0;

  return (
    <div className="flex flex-col gap-6">
      <DeckPageHeader
        eyebrow="Platform"
        title="Feature tracker"
        description="What each institute has asked for, and whether they can use it."
        actions={
          canEdit && (
            <Button
              variant="secondary"
              size="sm"
              onClick={() => openCatalogue(null)}
              leftIcon={<Plus className="h-4 w-4" />}
            >
              New feature
            </Button>
          )
        }
      />

      {/* `items-start` so the picker can stick at its own height rather than
          being stretched to match the board beside it. */}
      <div className="grid min-h-0 grid-cols-1 items-start gap-6 lg:grid-cols-[minmax(0,20rem)_minmax(0,1fr)]">
        <InstitutePicker
          institutes={rows}
          selectedUuid={selectedUuid}
          onSelect={setSelectedUuid}
          search={search}
          onSearchChange={setSearch}
          status={status}
          onStatusChange={setStatus}
          onClearFilters={() => {
            setSearch('');
            setStatus('');
          }}
          isLoading={isLoadingInstitutes}
          error={instituteError}
          onRetry={refetchInstitutes}
        />

        <section className="flex min-w-0 flex-col gap-4">
          {/* A skeleton only before the first board arrives. Switching
              institutes keeps the board in place under a loader instead —
              swapping it for a skeleton and back made the page blink. */}
          {(isLoadingBoard || isAwaitingSelection) && !board && (
            <div className="card p-5">
              <SkeletonLines count={10} />
            </div>
          )}

          {!isLoadingBoard && boardError && (
            <div className="card p-5">
              <QueryError
                title={
                  selectedUuid === linkedUuid
                    ? 'Could not open the institute in this link'
                    : "Could not load this institute's features"
                }
                error={boardError}
                onRetry={refetchBoard}
              />
            </div>
          )}

          {!isLoadingBoard && !isAwaitingSelection && !boardError && !board && (
            <div className="card p-5">
              <EmptyState
                icon={<LayoutGrid className="h-6 w-6" />}
                title="Choose an institute"
                description="Pick a customer on the left to see what they have asked for."
              />
            </div>
          )}

          {board && summary && !(boardError && !isLoadingBoard) && (
            <div className="relative" aria-busy={isLoadingBoard}>
              {/* Disabled while the next institute loads: the board on screen
                  is the previous one's, and a tick on it must not land. */}
              <fieldset
                disabled={isLoadingBoard}
                className={cn(
                  'm-0 flex min-w-0 flex-col gap-4 border-0 p-0 transition-opacity duration-150',
                  showSwitchLoader && 'pointer-events-none opacity-40',
                )}
              >
                <div className="card flex flex-col gap-4 overflow-hidden p-5">
                  {/* The identity strip, full-bleed through the card's padding so
                      it reads as the head of the panel rather than a first row.

                      Tinted, like the console's banners: everything below it is
                      one customer's record, and which customer that is should be
                      the least mistakable thing on a screen where the wrong tick
                      hands a feature to the wrong school.

                      (It used to be `bg-surface-2/50`, which painted nothing at
                      all — an opacity modifier on a `var()` colour compiles away
                      in this Tailwind setup.) */}
                  <DeckSurface className="-mx-5 -mt-5 border-b border-accent-line px-5 py-4">
                    <div className="flex flex-wrap items-center gap-3">
                      <span
                        aria-hidden
                        className="grid h-11 w-11 shrink-0 place-items-center rounded-card bg-gradient-to-br from-blue-500 via-indigo-600 to-sky-600 text-sm font-[650] text-white shadow-md shadow-blue-500/25"
                      >
                        {getNameInitials(board.institute.displayName)}
                      </span>

                      <div className="min-w-0 flex-1">
                        <h2 className="truncate text-lg text-ink">
                          {board.institute.displayName}
                        </h2>
                        <p className="truncate font-mono text-2xs text-ink-3">
                          {board.institute.code}
                        </p>
                      </div>

                      <Badge tone={SCHOOL_STATUS_TONE[board.institute.status]}>
                        {SCHOOL_STATUS_LABEL[board.institute.status]}
                      </Badge>

                      {/* What their features come to at list price — summed on
                          the server, never here, because it is money. */}
                      <div className="flex flex-col items-end leading-tight">
                        <span className="text-2xs uppercase tracking-label text-ink-3">Total</span>
                        <span className="text-lg font-[650] tabular-nums text-ink">
                          {formatCurrency(summary.totalPrice)}
                        </span>
                      </div>

                      {/* Nothing to hand over until something has been asked for. */}
                      {canEdit && (
                        <Button
                          variant="secondary"
                          size="sm"
                          disabled={summary.tracked === 0 || isBulkBusy || pendingUuid !== null}
                          onClick={() => setIsCopyOpen(true)}
                          leftIcon={<Copy className="h-3.5 w-3.5" />}
                        >
                          Copy to…
                        </Button>
                      )}
                    </div>
                  </DeckSurface>

                  <ProgressBar
                    label="Active"
                    value={activePct}
                    caption={
                      summary.tracked === 0
                        ? 'Nothing requested'
                        : `${summary.enabled}/${summary.tracked} (${Math.round(activePct)}%)`
                    }
                  />

                  <ChecklistToolbar
                    tracked={summary.tracked}
                    enabled={summary.enabled}
                    isBusy={isBulkBusy || pendingUuid !== null}
                    onSetAllEnabled={
                      canEdit
                        ? (next) =>
                            void runOnAll(
                              { isEnabled: next },
                              next
                                ? 'Every requested feature is active'
                                : 'Every requested feature is hidden',
                            )
                        : undefined
                    }
                  />
                </div>

                {/* Three numbers, in the platform console's own tile — the same
                    one the overview counts with, so our two screens count alike.
                    Only Active carries a drawn share, measured against what this
                    institute asked for. */}
                <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
                  <DeckStatTile
                    label="In catalogue"
                    value={summary.total}
                    hint="Everything the product offers"
                    icon={<LayoutGrid className="h-[18px] w-[18px]" />}
                  />
                  <DeckStatTile
                    label="Requested"
                    value={summary.tracked}
                    hint="What they asked for"
                    icon={<ClipboardList className="h-[18px] w-[18px]" />}
                  />
                  <DeckStatTile
                    label="Active"
                    value={summary.enabled}
                    tone={summary.tracked > 0 && summary.enabled === summary.tracked ? 'success' : 'default'}
                    hint="They can use it"
                    icon={<Eye className="h-[18px] w-[18px]" />}
                    share={summary.tracked > 0 ? summary.enabled / summary.tracked : undefined}
                    shareLabel="Share of what this institute asked for that they can use"
                  />
                </div>

                <div className="card p-5">
                  <FeatureChecklist
                    features={board.features}
                    canEdit={canEdit}
                    pendingUuid={pendingUuid}
                    isBusy={isBulkBusy}
                    selected={selected}
                    onToggleSelect={toggleSelected}
                    onSelectAll={selectAll}
                    onAddSelected={() => void addSelected()}
                    onEdit={openCatalogue}
                    onSet={(featureUuid, payload) =>
                      void runOnRow(featureUuid, () =>
                        featuresApi.set(board.institute.uuid, featureUuid, payload),
                      )
                    }
                    onRemove={(featureUuid) =>
                      void runOnRow(featureUuid, () =>
                        featuresApi.remove(board.institute.uuid, featureUuid),
                      )
                    }
                  />
                </div>
              </fieldset>

              {showSwitchLoader && (
                <div className="absolute inset-x-0 top-0 flex justify-center pt-24">
                  <div
                    role="status"
                    className="card flex items-center gap-3 px-4 py-3 shadow-lg"
                  >
                    <Loader size="sm" />
                    <span className="text-sm text-ink-2">
                      Loading {switchingTo ? `${switchingTo}'s` : 'their'} features…
                    </span>
                  </div>
                </div>
              )}
            </div>
          )}
        </section>
      </div>

      {canEdit && board && summary && (
        <CopyFeaturesModal
          isOpen={isCopyOpen}
          onClose={() => setIsCopyOpen(false)}
          source={board.institute}
          featureCount={summary.tracked}
          institutes={rows}
        />
      )}

      {canEdit && (
        <CatalogueEntryModal
          isOpen={isCatalogueOpen}
          feature={editing}
          onClose={() => setIsCatalogueOpen(false)}
          // A new row belongs on this board too, untracked, and a reworded one
          // changes what every row here says — so the board is read again rather
          // than guessed at.
          onSaved={refetchBoard}
        />
      )}
    </div>
  );
}
